/**
 * Voice Memo Detail View Component
 * Basecamp-like single memo detail view with associated documents
 */

import { useState } from 'react';
import { Sparkles, ChevronRight, FileText, Brain, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from './voice-memo-status-badge';
import { VoiceMemoPlayer } from './voice-memo-player';
import type { VoiceMemo, VoiceMemoPackage } from './types';

interface VoiceMemoDetailViewProps {
  memo: VoiceMemo;
  package?: VoiceMemoPackage;
  onViewTranscript?: () => void;
  onViewAnalysis?: () => void;
  onAnalyze?: () => void;
  onBack?: () => void;
}

export function VoiceMemoDetailView({
  memo,
  package: pkg,
  onViewTranscript,
  onViewAnalysis,
  onAnalyze,
  onBack
}: VoiceMemoDetailViewProps) {
  const hasTranscript = !!memo.transcript?.path;
  const hasAnalysis = memo.status === 'analyzed' || !!memo.summary;
  const relatedCount: number = 0; // TODO: Link to related documents

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header - Title is now in breadcrumb, so we just show status and actions */}
      <div className="px-6 pt-4 pb-4 border-b border-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <StatusBadge status={memo.status} />
            </div>
            <div className="flex items-center gap-2 mb-3">
              <StatusBadge status={memo.status} />
            </div>
          </div>
          {!hasAnalysis && onAnalyze && (
            <Button
              variant="default"
              className="gap-2"
              onClick={onAnalyze}
            >
              <Sparkles className="w-4 h-4" />
              Analyze
            </Button>
          )}
        </div>

        {/* Metadata Block */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <div className="text-muted-foreground">Date</div>
            <div className="font-medium">{memo.date}</div>
          </div>
          <div className="space-y-1">
            <div className="text-muted-foreground">Duration</div>
            <div className="font-medium">{memo.duration}</div>
          </div>
          {memo.fileSize && (
            <div className="space-y-1">
              <div className="text-muted-foreground">File Size</div>
              <div className="font-medium">{memo.fileSize}</div>
            </div>
          )}
          {memo.wordCount && (
            <div className="space-y-1">
              <div className="text-muted-foreground">Word Count</div>
              <div className="font-medium">{memo.wordCount} words</div>
            </div>
          )}
          {memo.theme && (
            <div className="space-y-1 col-span-2">
              <div className="text-muted-foreground">Theme</div>
              <Badge variant="secondary">{memo.theme}</Badge>
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      {memo.summary && (
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            SUMMARY
          </h2>
          <p className="text-sm text-foreground">{memo.summary}</p>
        </div>
      )}

      {/* Audio Player */}
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          AUDIO
        </h2>
        <div className="max-w-md">
          <VoiceMemoPlayer memo={memo} />
        </div>
      </div>

      {/* Associated Documents */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
        {/* Transcript Section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              TRANSCRIPT {hasTranscript ? '1' : '0'}
            </h2>
            {hasTranscript && (
              <button
                onClick={onViewTranscript}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                View transcript
                <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>
          {hasTranscript ? (
            <p className="text-sm text-muted-foreground">
              Transcript available
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">No transcript found</p>
          )}
        </div>

        {/* Analysis Section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              ANALYSIS {hasAnalysis ? '1' : '0'}
            </h2>
            {hasAnalysis && onViewAnalysis ? (
              <button
                onClick={onViewAnalysis}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              >
                View analysis
                <ChevronRight className="w-3 h-3" />
              </button>
            ) : onAnalyze ? (
              <button
                onClick={onAnalyze}
                className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" />
                Generate analysis
              </button>
            ) : null}
          </div>
          {hasAnalysis ? (
            <p className="text-sm text-muted-foreground">
              Analysis available
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">No analysis found</p>
          )}
        </div>

        {/* Related Documents Section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              RELATED {relatedCount}
            </h2>
            {relatedCount > 0 && (
              <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                View all related
              </button>
            )}
          </div>
          {relatedCount > 0 ? (
            <p className="text-sm text-muted-foreground">
              {relatedCount} related document{relatedCount !== 1 ? 's' : ''} found
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">No related documents</p>
          )}
        </div>

        {/* Actions Section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              ACTIONS {hasAnalysis ? '2' : '1'}
            </h2>
            <button className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              View all actions
            </button>
          </div>
          <div className="space-y-2">
            {!hasAnalysis && onAnalyze && (
              <button
                onClick={onAnalyze}
                className="w-full text-left px-3 py-2 text-sm border border-border rounded hover:bg-muted/50 transition-colors flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Analyze
              </button>
            )}
            {hasAnalysis && (
              <button
                onClick={onViewAnalysis}
                className="w-full text-left px-3 py-2 text-sm border border-border rounded hover:bg-muted/50 transition-colors flex items-center gap-2"
              >
                <Brain className="w-4 h-4" />
                Update Analysis
              </button>
            )}
            {hasAnalysis && relatedCount > 0 && (
              <p className="text-xs text-muted-foreground pt-1">
                + {relatedCount} more action{relatedCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
